<script lang="ts" setup>
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import { ref } from "vue";
import LightArrowIcon from "@/components/icon/LightArrowIcon.vue";
import { Media } from "@/types/entity";
import Hammer from "hammerjs";
import { nextTick } from "vue";
import { useMate } from "@/stores/mate";
import api from "@/modules/api";
import { showConfirmDialog, showLoadingToast } from "vant";
import i18n from "@/locales";

// Props
const emit = defineEmits<{
  (e: "onFinished", params: Media): void;
}>();

// State
const show = ref(false);
const initialImageUrl = ref("");
const main = ref<HTMLDivElement | null>(null);
const selectionMode = ref<"move" | "remove">("move");

let canvas: HTMLCanvasElement;
let ctx: CanvasRenderingContext2D;
let hammer: HammerManager;

const history = ref<string[]>([]);
let activeIdx = ref(0);

let lastScale = 1;
let offsetX = 0;
let offsetY = 0;
let lastOffsetX = 0;
let lastOffsetY = 0;
let size = 9;
let isDrawing = false;
let centerX = 0;
let centerY = 0;
let canvasWidth = 0;
let canvasHeight = 0;
const start = async (url?: string) => {
  if (url) initialImageUrl.value = url;

  show.value = true;
  nextTick(() => {
    const { width, height } = main.value!.getBoundingClientRect();
    // 选择最小的边作为画布的边长
    const ratio = 3 / 4;
    if (width / height > ratio) {
      canvasHeight = height;
      canvasWidth = height * ratio;
    } else {
      canvasWidth = width;
      canvasHeight = width / ratio;
    }
    canvas = document.getElementById("canvas") as HTMLCanvasElement;
    ctx = canvas.getContext("2d")!;
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    loadImg();
    nextTick(() => {
      if (!hammer) {
        hammer = new Hammer(canvas);
        hammer.get("pan").set({ direction: Hammer.DIRECTION_ALL });
        clickMode("move");
      }
    });
  });
};

const loadImg = () => {
  const img = new Image();
  img.crossOrigin = "Anonymous";
  img.src = initialImageUrl.value;
  const loading = showLoadingToast({
    message: i18n.global.t("Message.loading"),
  });
  img.onload = () => {
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
    loading.close();
    history.value.push(canvas.toDataURL());
    initTransform();
  };
};

const removePanStart = (e: HammerInput) => {
  isDrawing = true;
  draw(e);
};

const removePanMove = (e: HammerInput) => {
  if (isDrawing) {
    draw(e);
  }
};

const removePanEnd = (e: HammerInput) => {
  isDrawing = false;
  history.value.splice(activeIdx.value + 1);
  history.value.push(canvas.toDataURL());
  activeIdx.value++;
};

const movePanStart = (e: HammerInput) => {
  const { center } = e;
  centerX = center.x;
  centerY = center.y;
};

const movePanMove = (e: HammerInput) => {
  const { center } = e;
  const { x, y } = center;
  offsetX = x - centerX + lastOffsetX;
  offsetY = y - centerY + lastOffsetY;
  initTransform(offsetX, offsetY);
};

const movePanEnd = (e: HammerInput) => {
  lastOffsetX = offsetX;
  lastOffsetY = offsetY;
};

function draw(e: HammerInput) {
  const { center } = e;
  let { x, y } = center;
  x = x - canvas!.offsetLeft - lastOffsetX - main.value!.offsetLeft;
  y = y - canvas!.offsetTop - lastOffsetY - main.value!.offsetTop;
  x = x / lastScale;
  y = y / lastScale;
  let _size = size / lastScale;
  const half = _size / 2;
  ctx.clearRect(x - half, y - half, _size, _size);
}

const clickReset = async () => {
  try {
    await showConfirmDialog({
      title: i18n.global.t("ConfirmModal.areYouSureResetCanvas"),
      message: i18n.global.t("ConfirmModal.clearCanvas"),
      confirmButtonText: i18n.global.t("Buttons.confirm"),
      cancelButtonText: i18n.global.t("Buttons.cancel"),
    });
  } catch (e) {
    return;
  }
  history.value = [];
  activeIdx.value = 0;
  lastScale = 1;
  offsetX = 0;
  offsetY = 0;
  lastOffsetX = 0;
  lastOffsetY = 0;
  isDrawing = false;
  centerX = 0;
  centerY = 0;
  loadImg();
};

const clickPositionReset = () => {
  lastOffsetX = 0;
  lastOffsetY = 0;
  lastScale = 1;
  initTransform();
};

const clickConfirm = () => {
  canvas.toBlob(async (blob) => {
    const file = new File([blob!], "painting.png", { type: "image/png" });
    const loading = showLoadingToast({
      message: i18n.global.t("Message.uploading"),
    });
    const res = await api.uploadMedia(7, file, useMate().currentMate?.id);
    loading.close();
    emit("onFinished", res.item);
    show.value = false;
  });
};

const clickMode = (mode: "move" | "remove") => {
  selectionMode.value = mode;
  if (mode === "move") {
    hammer.off("panstart", removePanStart);
    hammer.off("panmove", removePanMove);
    hammer.off("panend", removePanEnd);
    hammer.on("panstart", movePanStart);
    hammer.on("panmove", movePanMove);
    hammer.on("panend", movePanEnd);
  } else {
    hammer.off("panstart", movePanStart);
    hammer.off("panmove", movePanMove);
    hammer.off("panend", movePanEnd);
    hammer.on("panstart", removePanStart);
    hammer.on("panmove", removePanMove);
    hammer.on("panend", removePanEnd);
  }
};

const clickScale = (scale: number) => {
  // 获取缩放比例
  lastScale = lastScale * scale;
  if (lastScale < 1) lastScale = 1;
  // 应用缩放变换
  initTransform();
  canvas.style.transformOrigin = 0 + "px " + 0 + "px";
};

const initTransform = (x = lastOffsetX, y = lastOffsetY) => {
  canvas.style.transform = `translate(${x}px,${y}px) scale(${lastScale})`;
};

const clickForward = (add = 1) => {
  if (activeIdx.value === 0 && add === -1) return;
  activeIdx.value += add;
  const url = history.value[activeIdx.value];
  let canvasPic = new Image();
  canvasPic.src = url;
  canvasPic.onload = function () {
    // 清除画布
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(canvasPic, 0, 0);
  };
};

export interface CustomPaintingEditorInstance {
  start: (url: string, initialStyle?: number) => void;
}

defineExpose({
  start,
});
</script>
<template>
  <VanPopup
    id="painting-overlay"
    :closeable="false"
    :show="show"
    class="grid grid-cols-[1fr] bg-base-100 h-full w-full"
    safe-area-inset-top
    position="top"
  >
    <div class="h-full grid grid-rows-[auto_1fr_auto] overflow-hidden">
      <threads-nav :show-center="false">
        <template #left>
          <button class="btn btn-ghost" @click="show = false">
            <light-arrow-icon />
          </button>
        </template>
        <template #center></template>
        <template #right>
          <button class="btn btn-sm btn-primary" @click="clickConfirm">
            {{ $t("Buttons.sure") }}
          </button>
        </template>
      </threads-nav>
      <div
        ref="main"
        class="overflow-hidden bg-neutral relative flex items-center justify-center"
        :style="`background: url(/img/mosaic.jpg)`"
      >
        <canvas id="canvas"></canvas>
      </div>
      <div class="flex gap-2 bg-base-200 p-2 w-full justify-between">
        <div class="flex gap-2">
          <button
            :class="['btn btn-xs', selectionMode === 'move' && 'btn-primary']"
            @click="clickMode('move')"
          >
            <i class="iconfont icon-location-arrow -rotate-90" />
          </button>
          <button
            :class="['btn btn-xs', selectionMode === 'remove' && 'btn-primary']"
            @click="clickMode('remove')"
          >
            <i class="iconfont icon-rubber-full" />
          </button>
          <button class="btn btn-xs" @click="clickScale(1.1)">
            <i class="iconfont icon-jia" />
          </button>
          <button class="btn btn-xs" @click="clickScale(0.9)">
            <i class="iconfont icon-jian" />
          </button>
          <button
            :disabled="activeIdx === 0"
            class="btn btn-xs"
            @click="clickForward(-1)"
          >
            <i class="iconfont icon-chehui" />
          </button>
          <button
            :disabled="activeIdx === history.length - 1"
            class="btn btn-xs"
            @click="clickForward(1)"
          >
            <i class="iconfont icon-chehui transform -scale-x-100" />
          </button>
          <!-- <div class="flex items-center">
            <button class="btn btn-xs" @click="clickScale(1.1)">
              <i class="iconfont icon-jia" />
            </button>
            <div>
              <h1>笔刷大小</h1>
              <p>{{ size }}</p>
            </div>
            <button class="btn btn-xs" @click="clickScale(0.9)">
              <i class="iconfont icon-jian" />
            </button>
          </div> -->
        </div>
        <div class="flex gap-2">
          <button class="btn btn-xs btn-neutral" @click="clickPositionReset">
            {{ $t("Buttons.locationReset") }}
          </button>
          <button class="btn btn-xs btn-neutral" @click="clickReset">
            {{ $t("Buttons.canvasReset") }}
          </button>
        </div>
      </div>
    </div>
  </VanPopup>
</template>
<style scoped></style>
